'use client'

import { imageQuality } from '@repo/sanity/image'
import { Button } from '@repo/ui/components/button'
import { cn } from '@repo/ui/lib/utils'
import { createHrefForDocument } from '@repo/url/href'
import { motion } from 'motion/react'
import { Image } from 'next-sanity/image'
import { LinkProps } from 'next/dist/client/link'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { AnchorHTMLAttributes, FunctionComponent, PropsWithChildren, useMemo, useState } from 'react'
import { PageSectionProps } from '../../../types/page.types'

const AllStoriesButton: FunctionComponent<PageSectionProps<'sectionArticleBrowser'>> = ({ section, pageDto }) => {
  const pathname = usePathname()
  const href = createHrefForDocument(section?.blog?._type, section?.blog?.urlSlug?.current, pageDto.params.locale)

  // No need to show 'All stories' button on index page.
  if (href === pathname) {
    return null
  }

  return (
    <Button variant='secondaryAuto' asChild={true}>
      <Link href={href} title={section?.blog?.seoPageTitle || undefined}>
        <span className='lg:hidden'>{pageDto.dictionary?.article?.allstories || 'All stories'}</span>
        <span className='hidden lg:inline'>{pageDto.dictionary?.article?.viewallstories || 'View all stories'}</span>
      </Link>
    </Button>
  )
}

const SectionArticleBrowser: FunctionComponent<PageSectionProps<'sectionArticleBrowser'>> = ({ section, pageDto, ...rest }) => {
  const [currentIndex, setCurrentIndex] = useState(1)
  const sortedArticles = useMemo(() => [section.articles?.[1], section.articles?.[0], section.articles?.[2]].filter((article) => !!article), [section.articles])

  if (sortedArticles.length !== 3) {
    return null
  }

  const handleClick = (index: number) => {
    setCurrentIndex(index)
  }

  const Wrapper: FunctionComponent<PropsWithChildren<{ linkProps?: LinkProps & AnchorHTMLAttributes<HTMLAnchorElement> }>> = ({ children, linkProps }) => {
    if (linkProps) {
      return (
        <Link {...linkProps} className='block size-full'>
          {children}
        </Link>
      )
    }

    return children
  }

  return (
    <ul className='relative flex aspect-[2/3] w-full items-center justify-center overflow-hidden bg-black text-white lg:aspect-[3/2]'>
      {sortedArticles.map((article, i) => {
        const position = (i - currentIndex + 3) % 3
        const isLeftSlide = position === 2
        const isCenterSlide = position === 0
        const isRightSlide = position === 1
        const href = createHrefForDocument('article', article.urlSlug?.current, pageDto.params.locale)
        const linkProps: LinkProps & AnchorHTMLAttributes<HTMLAnchorElement> = { href, title: article.seoPageTitle || undefined }

        return (
          <motion.li
            key={i}
            variants={{
              left: { x: 0, scale: 0.75, opacity: 0.7, left: 0, right: 'auto' },
              center: { x: '-50%', scale: 1, opacity: 1, left: '50%', right: 'auto' },
              right: { x: 0, scale: 0.75, opacity: 0.7, left: 'auto', right: 0 },
            }}
            data-position={position}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            initial={isCenterSlide ? 'center' : isLeftSlide ? 'left' : 'right'}
            animate={isCenterSlide ? 'center' : isLeftSlide ? 'left' : 'right'}
            onClick={() => !isCenterSlide && handleClick(i)}
            className={cn(
              'absolute h-5/6 lg:aspect-[3/2] lg:h-auto lg:w-2/3',
              isCenterSlide ? 'z-20 w-[calc(100%_-_(var(--spacing-miista-page-padding-content)_*_2))] origin-center' : 'z-10 w-full cursor-pointer',
              isLeftSlide && 'lg:ml-miista-page-padding-content origin-left',
              isRightSlide && 'lg:mr-miista-page-padding-content origin-right',
            )}
          >
            <article className='flex size-full flex-col gap-2 lg:gap-4'>
              <Wrapper linkProps={isCenterSlide ? linkProps : undefined}>
                <div className={cn('relative size-full overflow-hidden bg-neutral-900', isCenterSlide ? 'rounded-md' : 'lg:rounded-md')}>
                  {article.image?.asset?.url && (
                    <Image
                      fill={true}
                      priority={isCenterSlide}
                      quality={imageQuality}
                      className='object-cover'
                      src={article.image?.asset?.url}
                      alt={article.image?.alt || article.title || ''}
                      blurDataURL={article.image?.asset?.metadata?.lqip ?? undefined}
                      placeholder={article.image?.asset?.metadata?.lqip ? 'blur' : undefined}
                      sizes={isCenterSlide ? '(min-width: 1024px) 85vw, 95vw' : '(min-width: 1024px) 50vw, 75vw'}
                    />
                  )}
                  {(article.title || article.headline) && (
                    <header
                      className={cn(
                        'px-miista-page-padding absolute inset-x-0 top-1/2 flex -translate-y-1/2 flex-col items-center gap-4 text-center transition-opacity duration-200 ease-out lg:gap-6 xl:gap-10',
                        isCenterSlide ? 'opacity-100' : 'pointer-events-none opacity-0',
                      )}
                    >
                      {article.title && <h2 className='text-pretty text-lg uppercase lg:text-xl'>{article.title}</h2>}
                      {article.headline && <p className='text-balance text-4xl font-light italic md:text-5xl xl:text-7xl 2xl:text-9xl'>{article.headline}</p>}
                    </header>
                  )}
                </div>
              </Wrapper>
              <footer
                className={cn(
                  'flex flex-col items-center gap-2 transition-opacity duration-200 ease-out lg:flex-row lg:justify-between',
                  isCenterSlide ? 'opacity-100' : 'pointer-events-none opacity-0',
                )}
              >
                <p>{article.caption || article.title}</p>
                <div className='ml-auto mr-0 flex items-center'>
                  <Button variant='ghostAuto' asChild={true}>
                    <Link {...linkProps}>{pageDto.dictionary?.article?.readnow || 'Read now'}</Link>
                  </Button>
                  <AllStoriesButton pageDto={pageDto} section={section} {...rest} />
                </div>
              </footer>
            </article>
          </motion.li>
        )
      })}
    </ul>
  )
}

export default SectionArticleBrowser
